import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import ReactSelect from 'react-select';
import { Badge, Collapse, Label } from 'reactstrap';
import dayjs, { Dayjs } from 'dayjs';

import { WeeklyPlannerDatasetOptions as ViewOptions, WeeklyPlannerDateRangeOptions as RangeOptions } from '~constants/maps';

import { dateInputFieldFormat } from '~libs/dayjs';
import { useReduxSearch } from '~libs/searchParams';

import ClickableIcon from '~components/ClickableIcon';
import { SearchBar } from '~components/form-fields/search';
import { faMagnifyingGlass } from '~components/icon';
import WeekPicker from '~components/WeekPicker';

import userSearchFields from '~weekly-planner/forms/user/search';

import { patchParams, setSearchParams as setSearchParamsAction } from '~weekly-planner/reducers/weeklyPlanner';
import { selectParams, selectSearchParams } from '~weekly-planner/selectors';

import { SessionStatus } from '~rore/types';

interface ComponentProps {
  sessionId: string | undefined;
  createdAt: string;
  status: SessionStatus;
}

const STATUS_CONFIG: Record<SessionStatus, { color: string; label: string }> = {
  [SessionStatus.Processing]: { color: 'warning', label: 'Processing' },
  [SessionStatus.Solving]: { color: 'info', label: 'Solving' },
  [SessionStatus.Completed]: { color: 'success', label: 'Completed' },
  [SessionStatus.Failed]: { color: 'danger', label: 'Failed' },
};

const viewOptions = Object.values(ViewOptions).map((v) => ({ value: v, label: v }));
const rangeOptions = Object.values(RangeOptions).map((v) => ({ value: v, label: v }));

const RoREBanner: React.FC<ComponentProps> = ({ sessionId, createdAt, status }) => {
  const dispatch = useAppDispatch();
  const { date, viewType, rangeType } = useAppSelector(selectParams);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const [searchParams, setSearchParams] = useReduxSearch(
    'search',
    selectSearchParams,
    setSearchParamsAction,
    {},
    'object',
  );

  const { color, label } = STATUS_CONFIG[status];

  const onDateChange = (value: Dayjs) => {
    dispatch(patchParams({ date: value.format(dateInputFieldFormat) }));
  };

  return (
    <>
      <div className="nav d-flex justify-content-between bg-light p-2 mt-2 mb-2 banner">
        <div className="d-flex align-items-center gap-3">
          <div className="small">
            <strong>Session:</strong> {sessionId}
          </div>
          <Badge color={color} pill>
            {label}
          </Badge>
          <div className="small text-muted">
            <strong>Created:</strong> {createdAt}
          </div>
        </div>

        <div className="d-flex align-items-center">
          <Label className="fw-bold m-2">View:</Label>
          <ReactSelect
            id="rore-dataset-view-select"
            name="rore-dataset-view-select"
            options={viewOptions}
            value={viewOptions.find((o) => o.value === viewType)}
            defaultValue={viewOptions[0]}
            closeMenuOnSelect
            onChange={(o) => dispatch(patchParams({ viewType: o?.value }))}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 100 }) }}
          />
          <Label className="fw-bold m-2 ps-2">Range:</Label>
          <ReactSelect
            id="rore-dataset-range-select"
            name="rore-dataset-range-select"
            options={rangeOptions}
            value={rangeOptions.find((o) => o.value === rangeType)}
            defaultValue={rangeOptions[0]}
            closeMenuOnSelect
            onChange={(o) => dispatch(patchParams({ rangeType: o?.value }))}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 100 }) }}
          />
          <WeekPicker initialDate={dayjs(date)} onChange={onDateChange} />
          <ClickableIcon
            id="rore-search-toggle"
            icon={faMagnifyingGlass}
            size="small"
            className="mt-1 me-1"
            onClick={() => setIsSearchOpen((prev) => !prev)}
            toolTip={isSearchOpen ? 'Hide Search' : 'Search'}
          />
        </div>
      </div>

      <Collapse isOpen={isSearchOpen}>
        <SearchBar
          fields={userSearchFields}
          initialValues={searchParams ?? {}}
          storageKey="rore"
          onSubmit={setSearchParams}
        />
      </Collapse>
    </>
  );
};

export default RoREBanner;
